import { Game } from './core/Game.js';
import { metaProgression } from './systems/MetaProgression.js';
import { CHARACTERS } from './core/config.js';

// Game instance
let game;
let currentMenu = 'main';
let selectedCharacter = null;

// Menu management
const menus = {
    main: document.getElementById('mainMenu'),
    character: document.getElementById('characterSelectionMenu'),
    meta: document.getElementById('metaProgressionMenu'),
    game: document.getElementById('gameContainer')
};

// Initialize the application
async function init() {
    try {
        console.log('⚡ Initializing ASCII Survivors: The Glitch Realms...');
        
        // Wait for DOM to be fully loaded
        await new Promise(resolve => {
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', resolve);
            } else {
                resolve();
            }
        });
        
        // Setup menu system after DOM is loaded
        setupMenuSystem();
        
        // Update system time
        updateSystemTime();
        setInterval(updateSystemTime, 1000);
        
        // Show main menu first
        showMenu('main');
        
        console.log('✅ Application initialized successfully!');
    } catch (error) {
        console.error('❌ Failed to initialize application:', error);
        showError('Failed to start the game. Please refresh and try again.');
    }
}

function setupMenuSystem() {
    // Get fresh references to menu elements
    const menus = {
        main: document.getElementById('mainMenu'),
        character: document.getElementById('characterSelectionMenu'),
        meta: document.getElementById('metaProgressionMenu'),
        game: document.getElementById('gameContainer')
    };
    
    // Start Game button - now goes to character selection
    const startButton = document.getElementById('startGame');
    if (startButton) {
        startButton.addEventListener('click', () => {
            showMenu('character');
            populateCharacterSelection();
        });
    }
    
    // Character selection buttons
    const confirmCharacterButton = document.getElementById('confirmCharacter');
    const backToMainButton = document.getElementById('backToMain');
    
    if (confirmCharacterButton) {
        confirmCharacterButton.addEventListener('click', async () => {
            if (selectedCharacter) {
                showLoadingScreen();
                try {
                    await initializeGame(selectedCharacter);
                    showMenu('game');
                } catch (error) {
                    console.error('Failed to start game:', error);
                    showError('Failed to start game session.');
                    showMenu('main');
                }
            }
        });
    }
    
    if (backToMainButton) {
        backToMainButton.addEventListener('click', () => {
            showMenu('main');
        });
    }
    
    // Meta Progression button
    const metaButton = document.getElementById('metaMenu');
    if (metaButton) {
        metaButton.addEventListener('click', () => {
            showMenu('meta');
            populateMetaMenu();
        });
    }
    
    // Settings button (placeholder)
    const settingsButton = document.getElementById('settingsMenu');
    if (settingsButton) {
        settingsButton.addEventListener('click', () => {
            showNotification('⚙ Settings panel coming soon!', 'info');
        });
    }
    
    // About button (placeholder)
    const aboutButton = document.getElementById('aboutMenu');
    if (aboutButton) {
        aboutButton.addEventListener('click', () => {
            showAboutDialog();
        });
    }
    
    // Meta menu close button
    const closeMetaButton = document.getElementById('closeMetaMenu');
    if (closeMetaButton) {
        closeMetaButton.addEventListener('click', () => {
            showMenu('main');
        });
    }
    
    // Pause menu buttons
    const resumeButton = document.getElementById('resumeGame');
    const restartButton = document.getElementById('restartGame');
    const exitToMenuButton = document.getElementById('exitToMenu');
    
    if (resumeButton) {
        resumeButton.addEventListener('click', () => {
            resumeGame();
        });
    }
    
    if (restartButton) {
        restartButton.addEventListener('click', () => {
            restartGame();
        });
    }
    
    if (exitToMenuButton) {
        exitToMenuButton.addEventListener('click', () => {
            exitToMainMenu();
        });
    }
    
    // ESC key handling
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            handleEscapeKey();
        }
    });
}

async function initializeGame(characterData) {
    if (game) {
        // Clean up existing game
        game = null;
    }
    
    game = new Game(characterData);
    await game.init();
    
    // Set global reference for debugging
    window.game = game;
    
    return game;
}

function populateCharacterSelection() {
    const characterGrid = document.getElementById('characterGrid');
    if (!characterGrid) return;
    
    characterGrid.innerHTML = '';
    
    Object.values(CHARACTERS).forEach(character => {
        const characterDiv = document.createElement('div');
        characterDiv.className = 'character-option';
        characterDiv.dataset.characterId = character.id;
        
        characterDiv.innerHTML = `
            <div class="character-ascii" style="color: #${character.color.toString(16).padStart(6, '0')}">${character.char}</div>
            <div class="character-info">
                <h3>${character.name}</h3>
                <div class="character-description">${character.description}</div>
                <div class="character-stats">
                    <div class="character-stat">
                        <span>Health:</span>
                        <span>${Math.round(character.stats.healthMod * 100)}%</span>
                    </div>
                    <div class="character-stat">
                        <span>Speed:</span>
                        <span>${Math.round(character.stats.speedMod * 100)}%</span>
                    </div>
                    <div class="character-stat">
                        <span>Exp Gain:</span>
                        <span>${Math.round(character.stats.expMod * 100)}%</span>
                    </div>
                    <div class="character-stat">
                        <span>Weapon:</span>
                        <span>${character.startingWeapon.replace('_', ' ')}</span>
                    </div>
                </div>
                <div class="character-ability">${character.ability}</div>
            </div>
        `;
        
        characterDiv.addEventListener('click', () => {
            // Remove selection from other characters
            document.querySelectorAll('.character-option').forEach(opt => {
                opt.classList.remove('selected');
            });
            
            // Select this character
            characterDiv.classList.add('selected');
            selectedCharacter = character;
            
            // Enable confirm button
            const confirmButton = document.getElementById('confirmCharacter');
            if (confirmButton) {
                confirmButton.classList.remove('disabled');
            }
        });
        
        characterGrid.appendChild(characterDiv);
    });
}

function showMenu(menuName) {
    // Get fresh references to menu elements
    const menuElements = {
        main: document.getElementById('mainMenu'),
        character: document.getElementById('characterSelectionMenu'),
        meta: document.getElementById('metaProgressionMenu'),
        game: document.getElementById('gameContainer')
    };
    
    // Hide all menus
    Object.values(menuElements).forEach(menu => {
        if (menu) {
            menu.classList.remove('active');
        }
    });
    
    // Show target menu
    if (menuElements[menuName]) {
        menuElements[menuName].classList.add('active');
        currentMenu = menuName;
        
        // Apply special effects
        if (menuName === 'main') {
            playMenuSound();
        }
        
        // Force resize for game canvas when showing game
        if (menuName === 'game' && game) {
            setTimeout(() => {
                if (game.onWindowResize) {
                    game.onWindowResize();
                }
            }, 100);
        }
    }
}

function populateMetaMenu() {
    // Update meta progression stats
    const stats = metaProgression.getStats();
    
    // Update display elements
    const fragmentsDisplay = document.getElementById('fragmentsDisplay');
    const runsDisplay = document.getElementById('runsDisplay');
    const bestTimeDisplay = document.getElementById('bestTimeDisplay');
    
    if (fragmentsDisplay) {
        fragmentsDisplay.textContent = `Data Fragments: ${stats.totalFragments}`;
    }
    
    if (runsDisplay) {
        runsDisplay.textContent = `Debug Sessions: ${stats.totalRuns}`;
    }
    
    if (bestTimeDisplay) {
        const minutes = Math.floor(stats.bestTime / 60);
        const seconds = Math.floor(stats.bestTime % 60);
        bestTimeDisplay.textContent = `Best Runtime: ${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // Populate character list
    populateCharacterList();
    
    // Populate upgrades (if implemented)
    populateUpgradeList();
    
    // Populate achievements
    populateAchievementList();
}

function populateCharacterList() {
    const characterList = document.getElementById('characterList');
    if (!characterList) return;
    
    const characters = metaProgression.getCharacters();
    
    characterList.innerHTML = '';
    
    characters.forEach(character => {
        const card = document.createElement('div');
        card.className = 'character-card';
        
        if (character.unlocked) {
            if (character.id === metaProgression.selectedCharacter) {
                card.classList.add('selected');
            }
            
            card.addEventListener('click', () => {
                metaProgression.selectCharacter(character.id);
                populateCharacterList(); // Refresh to show selection
                showNotification(`✓ Selected: ${character.name}`, 'success');
            });
        } else {
            card.classList.add('locked');
        }
        
        card.innerHTML = `
            <div class="character-name">${character.name}</div>
            <div class="character-desc">${character.description}</div>
            <div class="character-cost">${character.unlocked ? 'UNLOCKED' : `Cost: ${character.cost} fragments`}</div>
        `;
        
        characterList.appendChild(card);
    });
}

function populateUpgradeList() {
    const upgradeList = document.getElementById('upgradeList');
    if (!upgradeList) return;
    
    upgradeList.innerHTML = '<div style="color: #888; text-align: center; padding: 20px;">Permanent upgrades system coming soon!</div>';
}

function populateAchievementList() {
    const achievementList = document.getElementById('achievementList');
    if (!achievementList) return;
    
    const achievements = metaProgression.getAchievements();
    
    achievementList.innerHTML = '';
    
    achievements.forEach(achievement => {
        const item = document.createElement('div');
        item.className = `achievement-item ${achievement.unlocked ? 'unlocked' : 'locked'}`;
        
        item.innerHTML = `
            <div class="achievement-name">${achievement.unlocked ? '✓' : '○'} ${achievement.name}</div>
            <div class="achievement-desc">${achievement.description}</div>
            <div class="achievement-reward">${achievement.unlocked ? 'COMPLETED' : `Reward: ${achievement.reward} fragments`}</div>
        `;
        
        achievementList.appendChild(item);
    });
}

function handleEscapeKey() {
    // Check if pause menu is currently active
    const pauseMenu = document.getElementById('pauseMenu');
    if (pauseMenu && pauseMenu.classList.contains('active')) {
        // Resume game if pause menu is open
        resumeGame();
        return;
    }
    
    if (currentMenu === 'game' && game) {
        // Show pause menu instead of exiting
        showPauseMenu();
    } else if (currentMenu === 'meta') {
        showMenu('main');
    } else if (currentMenu === 'character') {
        showMenu('main');
    }
}

function updateSystemTime() {
    const systemTime = document.getElementById('systemTime');
    if (systemTime) {
        const now = new Date();
        const timeString = now.toLocaleTimeString('en-US', { 
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        systemTime.textContent = `SYSTEM TIME: ${timeString}`;
    }
}

function showLoadingScreen() {
    showNotification('▶ Initializing debug session...', 'info', 2000);
}

function showNotification(message, type = 'info', duration = 3000) {
    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
        position: fixed;
        top: 80px;
        right: 20px;
        background: linear-gradient(135deg, rgba(0, 255, 136, 0.2), rgba(0, 255, 136, 0.4));
        border: 2px solid var(--primary-green);
        border-radius: 8px;
        padding: 15px 20px;
        color: var(--primary-green);
        font-family: var(--font-mono);
        font-size: 14px;
        font-weight: 700;
        z-index: 1000;
        backdrop-filter: blur(10px);
        box-shadow: 0 0 20px rgba(0, 255, 136, 0.4);
        animation: notificationSlide 0.3s ease-out;
        max-width: 300px;
        text-shadow: 0 0 10px currentColor;
    `;
    
    if (type === 'success') {
        notification.style.borderColor = '#00ff88';
        notification.style.color = '#00ff88';
    } else if (type === 'error') {
        notification.style.borderColor = '#ff4444';
        notification.style.color = '#ff4444';
    } else if (type === 'warning') {
        notification.style.borderColor = '#ffaa00';
        notification.style.color = '#ffaa00';
    }
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
        if (notification.parentNode) {
            notification.style.animation = 'notificationSlideOut 0.3s ease-in forwards';
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 300);
        }
    }, duration);
}

function showAboutDialog() {
    const aboutContent = `
        ▓▓▓ ASCII SURVIVORS: THE GLITCH REALMS ▓▓▓
        
        Version: 2.0.0 Enhanced Edition
        
        A cyberpunk survival game where you debug corrupted data
        entities in an ASCII-rendered digital realm.
        
        Features:
        • Unique ASCII art aesthetic
        • Auto-targeting combat system  
        • Meta progression with unlockable characters
        • Particle effects and screen shake
        • Achievement system
        • Persistent upgrades
        
        Controls:
        WASD/Arrows - Movement
        ESC - Pause/Resume game
        
        Created with Three.js and passion for retro aesthetics.
        
        ▓▓▓ SURVIVE THE CORRUPTION ▓▓▓
    `;
    
    const modal = document.createElement('div');
    modal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.8);
        backdrop-filter: blur(5px);
        z-index: 1000;
        display: flex;
        justify-content: center;
        align-items: center;
        animation: fadeIn 0.3s ease-out;
    `;
    
    const content = document.createElement('div');
    content.style.cssText = `
        background: linear-gradient(135deg, rgba(0, 30, 60, 0.95), rgba(0, 50, 80, 0.95));
        border: 3px solid var(--primary-green);
        border-radius: 15px;
        padding: 30px;
        color: var(--primary-green);
        font-family: var(--font-mono);
        font-size: 12px;
        max-width: 500px;
        text-align: center;
        white-space: pre-line;
        line-height: 1.6;
        backdrop-filter: blur(10px);
        box-shadow: 0 0 50px rgba(0, 255, 136, 0.4);
    `;
    
    content.textContent = aboutContent;
    
    modal.appendChild(content);
    document.body.appendChild(modal);
    
    // Close on click
    modal.addEventListener('click', () => {
        modal.style.animation = 'fadeOut 0.3s ease-in forwards';
        setTimeout(() => {
            if (modal.parentNode) {
                modal.parentNode.removeChild(modal);
            }
        }, 300);
    });
}

function showError(message) {
    showNotification(`❌ ${message}`, 'error', 5000);
}

function playMenuSound() {
    // Simple beep sound for menu interactions
    if (window.AudioContext || window.webkitAudioContext) {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();
            
            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);
            
            oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
            oscillator.type = 'sine';
            
            gainNode.gain.setValueAtTime(0, audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2);
            
            oscillator.start(audioContext.currentTime);
            oscillator.stop(audioContext.currentTime + 0.2);
        } catch (error) {
            // Silently fail if audio not supported
        }
    }
}

// Add CSS animations for notifications
const style = document.createElement('style');
style.textContent = `
    @keyframes notificationSlide {
        from { opacity: 0; transform: translateX(100%); }
        to { opacity: 1; transform: translateX(0); }
    }
    
    @keyframes notificationSlideOut {
        from { opacity: 1; transform: translateX(0); }
        to { opacity: 0; transform: translateX(100%); }
    }
    
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    
    @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
    }
`;
document.head.appendChild(style);

// Start the application
init();

function showPauseMenu() {
    if (!game || !game.gameState) return;
    
    // Pause the game
    game.gameState.gameRunning = false;
    
    // Update pause menu stats
    updatePauseMenuStats();
    
    // Show pause menu
    const pauseMenu = document.getElementById('pauseMenu');
    if (pauseMenu) {
        pauseMenu.classList.add('active');
    }
}

function hidePauseMenu() {
    const pauseMenu = document.getElementById('pauseMenu');
    if (pauseMenu) {
        pauseMenu.classList.remove('active');
    }
}

function updatePauseMenuStats() {
    if (!game || !game.gameState) return;
    
    const stats = game.gameState.stats;
    
    // Update time
    const minutes = Math.floor(stats.time / 60);
    const seconds = Math.floor(stats.time % 60);
    const timeElement = document.getElementById('pauseTimeValue');
    if (timeElement) {
        timeElement.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // Update level
    const levelElement = document.getElementById('pauseLevelValue');
    if (levelElement) {
        levelElement.textContent = stats.level;
    }
    
    // Update kills
    const killsElement = document.getElementById('pauseKillsValue');
    if (killsElement) {
        killsElement.textContent = stats.kills;
    }
    
    // Update health
    const healthElement = document.getElementById('pauseHealthValue');
    if (healthElement) {
        healthElement.textContent = `${Math.ceil(stats.health)}/${stats.maxHealth}`;
    }
}

function resumeGame() {
    hidePauseMenu();
    if (game && game.gameState) {
        game.gameState.gameRunning = true;
        game.animate(); // Restart the game loop
    }
}

function restartGame() {
    hidePauseMenu();
    // Reload the page to restart the game
    location.reload();
}

function exitToMainMenu() {
    hidePauseMenu();
    showMenu('main');
    if (game && game.gameState) {
        game.gameState.gameRunning = false;
    }
}

// Export for debugging
window.gameApp = {
    game,
    showMenu,
    showNotification,
    showPauseMenu,
    resumeGame,
    currentMenu
}; 